import { ChildData, ErrorMessage, MChildrenList, MIVConstants, ModelCompute, ModelUpdate, MRoot, MRootSystem, MUser } from "@1oo4/miv";
import { DocIDS } from "../ids";
import { DocClient } from "../docclient";
import { MChapter } from "./mdoc";

export class MDocUser extends MUser {

    constructor(data: ChildData) {
        super(data);
    }
}

export class MDocSystem extends MRootSystem<MDocUser> {

    constructor(client: DocClient) {
        super(client.users, client, "system root", client.fullLoad)
    }
}

export class MRootDoc extends MRoot {

    readonly Chapters: MChildrenList<MChapter>;

    constructor(client: DocClient) {
        super(DocIDS.ID_ROOT_DATA, DocIDS.TYPE_ROOT_DATA, client, "documentation root", client.docLoad, [client.fullLoad]);
        this.Chapters = new MChildrenList(this, MIVConstants.PROPERTY_ANY, client.chapters);
    }

    protected Updated(mu: ModelUpdate): void {
        if (mu.IsListChanged(this.Chapters)) {
            this.mustUpdateIndexing(mu);
            for (let i = 0; i < this.Chapters.Count; i++) {
                mu.SetDefaultLong(this.Chapters.Get(i).NumberIndexing, BigInt(i + 1));
            }
        }
    }

    AppendChapterAsync(): Promise<MChapter | undefined> {
        return this.AppendChildAsync((this.Client as DocClient).chapters, MIVConstants.PROPERTY_ANY);
    }

    mustUpdateIndexing(mu: ModelUpdate) {
        mu.SubscribeToDataUpdateCompleted(this);
    }
    protected DataUpdateCompleted(mc: ModelCompute): void {
        for (let ic = 0; ic < this.Chapters.Count; ic++) {
            const c = this.Chapters.Get(ic);
            mc.SetDefaultString(c.FullIndex, (ic + 1).toString())
            for (let ia = 0; ia < c.Articles.Count; ia++) {
                const a = c.Articles.Get(ia);
                mc.SetDefaultString(a.FullIndex, (ic + 1) + "." + (ia + 1))
                for (let is = 0; is < a.Sections.Count; is++) {
                    const s = a.Sections.Get(is);
                    mc.SetDefaultString(s.FullIndex, (ic + 1) + "." + (ia + 1) + "." + (is + 1))
                }
            }
        }
    }

    async GenerateWholeDocAsMarkdownAsync(): Promise<string | ErrorMessage> {
        const token = await this.Client.TakeLockAsync();
        const error = await this.LoadChildrenAsync((this.Client as DocClient).fullLoad, token);
        if (error !== undefined) {
            return error;
        }
        let md = '# MIV documentation';
        for (let ic = 0; ic < this.Chapters.Count; ic++) {
            const chapter = this.Chapters.Get(ic);
            md += '\n\n## [' + chapter.NumberIndexing.Value + '] ' + chapter.Name.Value;
            for (let ia = 0; ia < chapter.Articles.Count; ia++) {
                const article = chapter.Articles.Get(ia);
                md += '\n\n### [' + chapter.NumberIndexing.Value + '.' + article.NumberIndexing.Value + '] ' + article.Name.Value;
                for (let is = 0; is < article.Sections.Count; is++) {
                    const section = article.Sections.Get(is);
                    md += '\n\n#### [' + chapter.NumberIndexing.Value + '.' + article.NumberIndexing.Value + '.' + section.NumberIndexing.Value + '] ' + section.Name.Value;
                    for (let i = 0; i < section.Contents.Count; i++) {
                        const content = section.Contents.Get(i);
                        md += content.toMarkdown();
                    }
                }
            }
        }
        this.UnloadChildrenAsync((this.Client as DocClient).fullLoad, token)
        this.Client.ReleaseLock();
        return md;
    }
    async GenerateWholeDocAsHTMLAsync(): Promise<string | ErrorMessage> {
        const token = await this.Client.TakeLockAsync();
        const error = await this.LoadChildrenAsync((this.Client as DocClient).fullLoad, token);
        if (error !== undefined) {
            return error;
        }
        let html = '<html><head><tile>MIV documentation</title></head><body>';
        for (let ic = 0; ic < this.Chapters.Count; ic++) {
            const chapter = this.Chapters.Get(ic);
            html += '<div><h1 id="' + chapter.ID + '">' + chapter.Name.Value + '</h1>';
            for (let ia = 0; ia < chapter.Articles.Count; ia++) {
                const article = chapter.Articles.Get(ia);
                html += '<div><h2 id="' + article.ID + '">' + article.Name.Value + '</h2>';
                for (let is = 0; is < article.Sections.Count; is++) {
                    const section = article.Sections.Get(is);
                    html += '<div><h3 id="' + section.ID + '">' + section.Name.Value + '</h3>';
                    for (let i = 0; i < section.Contents.Count; i++) {
                        const content = section.Contents.Get(i);
                        html += content.toHTML();
                    }
                    html += '</div>'
                }
                html += '</div>'
            }
            html += '</div>'
        }
        html += '</body></html>';
        this.UnloadChildrenAsync((this.Client as DocClient).fullLoad, token)
        this.Client.ReleaseLock();
        return html;
    }

    async DownloadWholeDocMarkdownAsync(): Promise<ErrorMessage | undefined> {
        const md = await this.GenerateWholeDocAsMarkdownAsync();
        if (md instanceof ErrorMessage) {
            return md;
        }
        MRootDoc.downloadFile(md, "text/markddown;charset=utf-8", "miv_doc.md");
        return undefined;
    }
    async DownloadWholeDocHTMLAsync(): Promise<ErrorMessage | undefined> {
        const html = await this.GenerateWholeDocAsHTMLAsync();
        if (html instanceof ErrorMessage) {
            return html;
        }
        MRootDoc.downloadFile(html, "text/html;charset=utf-8", "miv_doc.html");
        return undefined;
    }
    private static downloadFile(content: string, contentType: string, fileName: string) {
        const blob = new Blob([content], { type: contentType });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;

        // Prevent SPA router from intercepting this click
        link.addEventListener("click", (e) => e.stopPropagation());

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }
}